import axios from "axios";
import * as React from "react";
import { lazy, Suspense } from "react";
import type { PageData } from "../../js/data/pageData.ts";
import { ItemWrapperTypeBase } from "../../js/enums.ts";
import { getAllEnv, getEnv } from "../../js/helpers/env.ts";
import { objectClone } from "../../js/helpers/utils.ts";
import type { PageItemData, PageRenderedItemData } from "../../js/page/content.types";
import {
	getRepoObjectWithId,
	preparePageForRender,
	type RenderOptions,
	type RenderStateHelper,
	renderTags,
} from "../../js/render/renderFunctionsData.ts";
import { ItemTypeCustom, type ItemWrapperType, ItemWrapperTypeCustom } from "../enumCustom.ts";
import NewsBox from "../frontend/newsBox.tsx";
import PageBox from "../frontend/pageBox";
import RenderMenuItem from "../frontend/renderMenuItem.tsx";

const Service = lazy(() => import('../frontend/service.tsx'));
const Survey = lazy(() => import('../frontend/survey.tsx'));
const LatestNews = lazy(() => import('../frontend/latestNews.tsx'));
const Contact = lazy(() => import('../form/contactForm.tsx'));
const Callback = lazy(() => import('../form/callbackForm.tsx'));
const MapWithSingleMarker = lazy(() => import('../frontend/mapWithSingleMarker.tsx'));
const MapDelivery = lazy(() => import('../frontend/mapDelivery.tsx'));

export const itemClassDefault = ['h-full']; // TODO: h-full is useful only when section has align-items: stretch

export default function renderItemCustom(item: PageRenderedItemData, indexSection?: number, indexItem?: number) {
	switch (item?.type) {
		case ItemTypeCustom.CONTACT:
			return <Contact/>
		case ItemTypeCustom.NEWSLETTER:
			return <Callback/>
		case ItemTypeCustom.SURVEY:
			return <Survey content={item.content}/>

		case ItemTypeCustom.LATEST:
			return <LatestNews item={item}/>
		case ItemTypeCustom.NEWS:
			return item?.page && <NewsBox news={item.page}/>

		case ItemTypeCustom.PAGEBOX:
			return item?.page ? <PageBox item={item.page}/> : ''
		case ItemTypeCustom.SERVICE:
			return item?.page ? <Service item={item.page} indexSection={indexSection} indexItem={indexItem}/> : ''

		case ItemTypeCustom.MAP:
			return <Suspense fallback=""><MapWithSingleMarker latitude={51.77292422069688} longitude={-1.3582575423285699} className="w-full h-[430px] md:h-[560px]"/></Suspense>
		case ItemTypeCustom.MAP_DELIVERY:
			return <Suspense fallback=""><MapDelivery className="w-full h-[430px] md:h-[560px]"/></Suspense>

		case ItemTypeCustom.MENUITEM:
			return <RenderMenuItem item={item}/>

		case ItemTypeCustom.DOWNLOADS:
			return <Contact/>
	}

	return null;
}

export async function fetchLatestNews(amount: number, renderStateHelper: RenderStateHelper, options: RenderOptions = {}) {
	return axios.get(getEnv('PUBLIC_API_HOST') + '/api/news/' + amount).then((response) => {
		return Promise.all(response.data.map((page: PageData) => {
			return preparePageForRender(page, renderStateHelper, options);
		}));
	});
}

export async function prepareContentItemsForRenderCustom(item: PageItemData, renderStateHelper: RenderStateHelper, options: RenderOptions = {}) {
	switch (item.type) {
		case ItemTypeCustom.NEWS:
		case ItemTypeCustom.PAGEBOX:
		case ItemTypeCustom.SERVICE:
			if (!options?.renderFlat && item.content) {
				if (options.hasOwnProperty('skipReusable') === false) {
					options.skipReusable = [];
				}

				if (options?.skipReusable?.includes(Number.parseInt(item.content)) === false) {
					//console.log("loading item reusable: " + item.content, options);
					item.page = await getRepoObjectWithId('page', Number.parseInt(item.content), renderStateHelper, {
						...objectClone(options),
						renderFlat: ItemTypeCustom.SERVICE === item.type // render services flat, slider and reusable are ok to load fully
					}).then((data) => {
						options.skipReusable.push(Number.parseInt(item.content));
						return data;
					});
				} else {
					console.log('item skipping loading page: ' + item.content);
				}
			}
			break;

		case ItemTypeCustom.LATEST:
			if (!options?.renderFlat) {
				const amount = (Number.parseInt(item.content) ? Number.parseInt(item.content) : 3);
				item.latestNews = await fetchLatestNews(amount, renderStateHelper, {...objectClone(options), renderFlat: true});
			}
			break;

		case ItemTypeCustom.MENUITEM:
			if (!options?.skipState && item.editorState) {
				try {
					const state = renderStateHelper.editor.parseEditorState(item.editorState);
					item.rendered = renderStateHelper.renderEditorState(renderStateHelper.editor, state);
					item.rendered = renderTags(item.rendered, {
						...getAllEnv(),
						...options.tags
					});
				} catch {
					console.info('wrong text item state:');
					console.dir(item.editorState, {depth: 3});
				}
			}

			if (!options?.skipData) {
				if (item.imageId) {
					item.image = await getRepoObjectWithId('asset', item.imageId);
				}
			}
			break;
	}

	return item;
}

export function renderItemWrapperCustom(wrapper: ItemWrapperType, rendered: React.ReactNode) {
	console.log(wrapper);

	switch (wrapper) {
		case ItemWrapperTypeCustom.BLOCKQUOTE:
			return <blockquote className="md:text-center">
				<div className="stylistic-quote-mark-before" aria-hidden="true"/>
				{rendered}
			</blockquote>;

		case ItemWrapperTypeCustom.HEADER_BOX_BIG:
			console.log("HEADER_BOX_BIG");
			return <div className="headerBox big">
				<div>{rendered}</div>
			</div>;
	}

	return rendered;
}
